import { memo, useCallback, useMemo } from "react";
import { getModifierGroupDescription } from "../../helpers";
import {
  CartItemModifierGroupModel,
  CartItemModifierModel,
  StoreModifierGroupModel,
  StoreModifierModel,
} from "../../models";
import ModifierPrice from "./ModifierPrice";
import clsx from "clsx";
import { FaMinus, FaPlus } from "react-icons/fa";

type Props = {
  index: number;
  inflationContext: {
    typeId: number | string;
    hasAdInflation: boolean;
    productPrice: number;
    productBasePrice: number;
  };
  modifierGroup: StoreModifierGroupModel;
  selectedModifiers: CartItemModifierGroupModel[];
  errors?: {
    [g_id: string]: {
      g_name: string;
      message: string;
    };
  };
  handleSelectModifier: (
    modGroup: StoreModifierGroupModel,
    modifier: CartItemModifierModel,
  ) => void;
};

const QuantityModifierGroupComponent: React.FC<Props> = ({
  index: groupIndex,
  inflationContext,
  modifierGroup,
  errors = {},
  selectedModifiers,
  handleSelectModifier,
}) => {
  const {
    g_id: groupId,
    g_name: groupName,
    modifier: modifiers,
  } = modifierGroup;

  const hasError = !!errors[groupId];
  const groupDescription = useMemo(
    () => getModifierGroupDescription(modifierGroup),
    [modifierGroup],
  );

  return (
    <>
      <div className="addsize_pizza">
        <h4>
          <label className={hasError ? "text-danger" : ""}>{groupName}</label>
          <span className={hasError ? "text-danger" : ""}>
            {groupDescription}
          </span>
        </h4>

        <ul className="row modifier-product">
          {modifiers.map((modifier, modifierIndex) => {
            return (
              <ModifierComponent
                key={modifierIndex}
                groupIndex={groupIndex}
                modifier={modifier}
                modifierGroup={modifierGroup}
                selectedModifiers={selectedModifiers}
                handleSelectModifier={handleSelectModifier}
                inflationContext={inflationContext}
              />
            );
          })}
        </ul>
      </div>
    </>
  );
};

type ModifierComponentProps = {
  groupIndex: number;
  modifier: StoreModifierModel;
  modifierGroup: StoreModifierGroupModel;
  selectedModifiers: CartItemModifierGroupModel[];
  handleSelectModifier: (
    modGroup: StoreModifierGroupModel,
    modifier: CartItemModifierModel,
  ) => void;
  inflationContext: {
    typeId: number | string;
    hasAdInflation: boolean;
    productPrice: number;
    productBasePrice: number;
  };
};

const ModifierComponent: React.FC<ModifierComponentProps> = ({
  modifier,
  groupIndex,
  modifierGroup,
  selectedModifiers,
  handleSelectModifier,
  inflationContext,
}) => {
  const { g_id: groupId, g_max_selection } = modifierGroup;
  const { m_id: modifierId, m_name: modifierName } = modifier;

  // Derive cart modifier directly from props to avoid first-render desync
  const cartModifier = useMemo<CartItemModifierModel>(() => {
    return (
      selectedModifiers
        .find((g) => g.g_id === groupId)
        ?.modifiers.find((m) => m.m_id === modifierId) ?? {
        m_id: modifier.m_id,
        m_name: modifier.m_name,
        m_price: modifier.m_price,
        m_qty: 1,
        prices: [],
        selected: false,
      }
    );
  }, [selectedModifiers, groupId, modifier]);

  // Memoize modifier selection logic to avoid recalculation
  const isSelected = useMemo(
    () =>
      selectedModifiers.some(
        (group) =>
          group.g_id === groupId &&
          group.modifiers.some((m) => m.m_id === modifier.m_id),
      ),
    [selectedModifiers, groupId, modifier.m_id],
  );

  // ✅ Total selected modifiers in the group
  const selectedCount = useMemo(
    () =>
      selectedModifiers
        .find((group) => group.g_id === groupId)
        ?.modifiers.reduce(
          (total, modifier) => total + (modifier.m_qty || 1),
          0,
        ) ?? 0,
    [selectedModifiers, groupId],
  );

  // ✅ Quantity handlers (no mutation)
  const handleDecrease = useCallback(() => {
    const updatedCartModifier: CartItemModifierModel = {
      ...cartModifier,
      m_qty: Math.max(1, cartModifier.m_qty - 1),
    };
    handleSelectModifier(modifierGroup, updatedCartModifier);
  }, [cartModifier, modifierGroup, handleSelectModifier]);

  const handleIncrease = useCallback(() => {
    const updatedCartModifier: CartItemModifierModel = {
      ...cartModifier,
      m_qty: cartModifier.m_qty + 1,
    };
    handleSelectModifier(modifierGroup, updatedCartModifier);
  }, [cartModifier, modifierGroup, handleSelectModifier]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const updatedCartModifier: CartItemModifierModel = {
        ...cartModifier,
        selected: e.target.checked,
      };
      handleSelectModifier(modifierGroup, updatedCartModifier);
    },
    [cartModifier, modifierGroup, handleSelectModifier],
  );

  return (
    <>
      <li key={modifierId} className="col-12 col-md-12">
        <label>
          <input
            type={g_max_selection == 1 ? "radio" : "checkbox"}
            name={`modifier-group-${groupId}`}
            value={modifier.m_id}
            checked={isSelected}
            onChange={handleChange}
            disabled={
              !isSelected &&
              g_max_selection > 0 &&
              g_max_selection != 1 &&
              selectedCount >= g_max_selection
            }
          />{" "}
          <span className="w-100 d-flex justify-content-between align-items-start flex-wrap">
            {/* <span className={clsx('d-flex flex-column', (!isSelected || isEmpty(modifier.prices)) && "flex-sm-row justify-content-between w-100")}> */}
            <span
              className={clsx(
                "d-flex flex-column",
                "flex-sm-row justify-content-between w-100",
              )}
            >
              {/* Modifier Name */}
              <span className="modifier-name">{modifierName}</span>

              {/* Price */}
              {(Number(modifier.m_price) > 0 || cartModifier.m_price > 0) && (
                <span
                  className={clsx("modifier-price", !isSelected && "ms-auto")}
                >
                  <span>
                    <ModifierPrice
                      inflationContext={inflationContext}
                      groupIndex={groupIndex}
                      modifier={modifier}
                      cartModifier={cartModifier}
                    />
                  </span>
                </span>
              )}
            </span>
          </span>
        </label>

        {/* Quantity Selector (if selected) */}
        {isSelected && (
          <div className="modifier-count d-flex align-items-center ms-auto mt-1">
            <button type="button" onClick={handleDecrease}>
              <FaMinus />
            </button>
            <span className="number_count mx-2">{cartModifier.m_qty || 1}</span>
            <button
              type="button"
              onClick={handleIncrease}
              disabled={g_max_selection > 0 && selectedCount >= g_max_selection}
            >
              <FaPlus />
            </button>
          </div>
        )}
      </li>
    </>
  );
};

export const QuantityModifierGroup = memo(QuantityModifierGroupComponent);
